import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { Cooperation } from '@app/shared/types/models/cooperation/cooperation.model';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { CooperationsDataService } from '../cooperations-data.service';
import { CooperationDTO } from '../dtos/cooperation/cooperation.dto';
import { CreateCooperationRequest } from '../dtos/cooperation/create-cooperation-request.dto';
import { CooperationFactory } from '../factories/cooperation.factory';
import { CooperationsHttpService } from '../http/cooperations-http.service';
import { NotificationsService } from '../notifications.service';

/**
 * CooperationsActionsService - Facade for cooperation mutation operations.
 *
 * This service centralizes user-triggered actions that modify cooperation data
 * and handles side effects such as loaders and toast notifications.
 *
 * Architecture:
 * - {@link CooperationsActionsService}: User actions, mutations, side effects (toasts, loader)
 * - {@link CooperationsDataService}: Data access, queries, cache/store management
 * - {@link CooperationsHttpService}: HTTP API calls
 */
@Injectable({ providedIn: 'root' })
export class CooperationActionsService {
  private readonly http = inject(CooperationsHttpService);
  private readonly factory = inject(CooperationFactory);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly loaderService = inject(LoaderService);
  private readonly notificationService = inject(NotificationsService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly cooperationsDataService = inject(CooperationsDataService);

  public create(companyId: string, data: CreateCooperationRequest): Observable<Cooperation | undefined> {
    if (!companyId || !data) return of(undefined);

    this.loaderService.startLoading();
    return this.http.create(companyId, data).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((dto: CooperationDTO) => {
        const result = this.cooperationsDataService.upsertOutgoing(companyId, dto);
        this.toastService.success(this.translateService.instant('cooperations.toasts.successfully-created'));
        return result;
      }),
      catchError(() => of(undefined)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public accept(cooperationId: string, companyId: string): Observable<Cooperation | undefined> {
    if (!cooperationId || !companyId) return of(undefined);

    this.loaderService.startLoading();
    return this.http.accept(cooperationId).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((dto: CooperationDTO) => {
        const result = this.cooperationsDataService.upsertLocalData(dto);
        this.toastService.success(this.translateService.instant('cooperations.toasts.accepted'));

        this.notificationService.notify({
          eventType: 'CooperationAccepted',
          data: result()
        });

        return result() ?? undefined;
      }),
      catchError(() => of(undefined)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public reject(cooperationId: string, companyId: string): Observable<Cooperation | undefined> {
    if (!cooperationId || !companyId) return of(undefined);

    this.loaderService.startLoading();
    return this.http.reject(cooperationId).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((dto: CooperationDTO) => {
        const result = this.cooperationsDataService.upsertLocalData(dto);
        this.toastService.success(this.translateService.instant('cooperations.toasts.rejected'));
        this.notificationService.notify({
          eventType: 'CooperationRejected',
          data: result()
        });
        return result() ?? undefined;
      }),
      catchError(() => of(undefined)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public deactivate(cooperationId: string, companyId: string): Observable<Cooperation | undefined> {
    if (!cooperationId || !companyId) return of(undefined);

    this.loaderService.startLoading();
    return this.http.deactivate(cooperationId).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((dto: CooperationDTO) => {
        const result = this.cooperationsDataService.upsertLocalData(dto);
        this.toastService.success(this.translateService.instant('cooperations.toasts.deactivated'));
        this.notificationService.notify({
          eventType: 'CooperationDeactivated',
          data: result()
        });
        return result() ?? undefined;
      }),
      catchError(() => of(undefined)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  /** Set the list of project IDs linked to the cooperation (replaces existing). */
  public setCooperationProjects(cooperationId: string, projectIds: string[]): Observable<boolean> {
    if (!cooperationId) return of(false);

    this.loaderService.startLoading();
    return this.http.postCooperationProjects(cooperationId, projectIds).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(dtos => {
        const result = this.cooperationsDataService.upsertCooperationProjects(cooperationId, dtos);
        this.toastService.success(this.translateService.instant('cooperations.toasts.projects-updated'));

        this.notificationService.notify({
          eventType: 'CooperationProjectsUpdated',
          data: result()
        });

        return true;
      }),
      catchError(() => of(false)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  /** Set the list of group IDs linked to the cooperation (replaces existing). */
  public setCooperationGroups(cooperationId: string, groupIds: string[]): Observable<boolean> {
    if (!cooperationId) return of(false);

    this.loaderService.startLoading();
    return this.http.postCooperationGroups(cooperationId, groupIds).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(dtos => {
        const result = this.cooperationsDataService.upsertCooperationGroups(cooperationId, dtos);
        this.toastService.success(this.translateService.instant('cooperations.toasts.groups-updated'));

        this.notificationService.notify({
          eventType: 'CooperationGroupsUpdated',
          data: result()
        });

        return true;
      }),
      catchError(() => of(false)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  /** Add one user to the cooperation. */
  public addCooperationUser(cooperationId: string, applicationUserId: string): Observable<boolean> {
    if (!cooperationId || !applicationUserId) return of(false);

    this.loaderService.startLoading();
    return this.http.postCooperationUser(cooperationId, { applicationUserId }).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(dto => {
        const result = this.cooperationsDataService.upsertCooperationUser(dto);
        this.toastService.success(this.translateService.instant('cooperations.toasts.users-updated'));

        this.notificationService.notify({
          eventType: 'CooperationUserAdded',
          data: result()
        });

        return true;
      }),
      catchError(() => of(false)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  /** Add multiple users to the cooperation (one POST per user). */
  public addCooperationUsers(cooperationId: string, applicationUserIds: string[]): Observable<boolean> {
    if (!cooperationId || !applicationUserIds?.length) return of(false);

    this.loaderService.startLoading();
    const requests = applicationUserIds.map(applicationUserId =>
      this.http.postCooperationUser(cooperationId, { applicationUserId })
    );
    return forkJoin(requests).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(dtos => {
        dtos.forEach(dto => {
          const result = this.cooperationsDataService.upsertCooperationUser(dto);
          this.notificationService.notify({
            eventType: 'CooperationUserAdded',
            data: result()
          });
        });
        this.toastService.success(this.translateService.instant('cooperations.toasts.users-updated'));

        return true;
      }),
      catchError(() => of(false)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  /** Remove a user from the cooperation. */
  public removeCooperationUser(cooperationId: string, cooperationUserId: string): Observable<boolean> {
    if (!cooperationId || !cooperationUserId) return of(false);

    this.loaderService.startLoading();
    return this.http.deleteCooperationUser(cooperationId, cooperationUserId).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(() => {
        this.cooperationsDataService.removeCooperationUser(cooperationId, cooperationUserId);
        this.toastService.success(this.translateService.instant('cooperations.toasts.users-updated'));
        this.notificationService.notify({
          eventType: 'CooperationUserRemoved',
          data: cooperationUserId
        });
        return true;
      }),
      catchError(() => of(false)),
      finalize(() => this.loaderService.stopLoading())
    );
  }
}
